import type { Plan, PlanDay, PlanType, Ridden } from '@painpal/contracts';
import { addDays, daysBetween, estimatePlan, loadPerHour, PLAN_DURATION_S, PLAN_TABLE, weekStart } from '@painpal/core';
import { formatHoursMinutes, formatWeekdayDayMonth, formatWhole, weekdayName } from '../../lib/format';
import { plural } from '../../lib/plural';
import { weekRangeLabel } from '../fitness/weekSnapshotModel';

export const PLAN_LABELS: Record<PlanType, string> = {
  rest: 'REST', recovery: 'RECOVERY', endurance: 'ENDURANCE', tempo: 'TEMPO',
  'sweet-spot': 'SWEET SPOT', threshold: 'THRESHOLD', 'vo2-max': 'VO2 MAX', race: 'RACE',
};

export type DurationInput = { ok: true; seconds: number | null } | { ok: false; error: string };

const H_MM = /^(\d{1,2})(?::([0-5]\d))?$/;
const RANGE_ERROR = `Duration must be between ${formatHoursMinutes(PLAN_DURATION_S.min)} and ${formatHoursMinutes(PLAN_DURATION_S.max)}`;

// Phone number pads have no colon key, so the field takes digits only and writes the colon itself: 115 → 1:15.
export function maskDuration(text: string): string {
  const digits = text.replace(/\D/g, '').slice(0, 4);
  return digits.length < 3 ? digits : `${digits.slice(0, -2)}:${digits.slice(-2)}`;
}

export function parseDuration(text: string): DurationInput {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, seconds: null };
  const match = H_MM.exec(trimmed);
  if (match === null) return { ok: false, error: 'Enter the duration as H:MM, e.g. 1:15' };
  const seconds = Number(match[1]) * 3600 + Number(match[2] ?? 0) * 60;
  if (seconds < PLAN_DURATION_S.min || seconds > PLAN_DURATION_S.max) return { ok: false, error: RANGE_ERROR };
  return { ok: true, seconds };
}

// "~113", but a plain "0" for Rest or an empty week, as the board writes them.
export const formatEstimate = (load: number): string => (Math.round(load) === 0 ? '0' : `~${formatWhole(load)}`);

export const defaultDuration = (type: PlanType): string => formatHoursMinutes(PLAN_TABLE[type].defaultS);

// The live estimate's working, e.g. "46 / H × 2:30", with the type's default duration when none is typed.
export function estimateMath(type: PlanType, seconds: number | null): string {
  if (type === 'rest') return 'REST · NO LOAD';
  const estimate = estimatePlan(type, seconds);
  return `${formatWhole(loadPerHour(type))} / H × ${formatHoursMinutes(estimate.durationS)}${estimate.defaulted ? ' DEFAULT' : ''}`;
}

export interface PlanDuration { text: string; defaulted: boolean }

export function planDuration(plan: Plan): PlanDuration | null {
  if (plan.type === 'rest') return null;
  return { text: formatHoursMinutes(plan.durationS ?? PLAN_TABLE[plan.type].defaultS), defaulted: plan.defaulted };
}

export function planSummary(plan: Plan): string {
  const duration = planDuration(plan);
  const time = duration === null ? '' : ` ${duration.text}${duration.defaulted ? ' DEFAULT' : ''}`;
  const note = plan.note === null ? '' : ` · ${plan.note}`;
  return `${PLAN_LABELS[plan.type]}${time}${note} · ${formatEstimate(plan.estimatedLoad)} LOAD`;
}

export function rowLabel(day: PlanDay): string {
  const status = day.status === null ? '' : ` · ${day.status.toUpperCase()}`;
  return `${formatWeekdayDayMonth(day.date)} · ${day.plan === null ? 'NOTHING PLANNED' : planSummary(day.plan)}${status}`;
}

export function editorEyebrow(day: PlanDay): string {
  if (day.status !== null) return `EDIT DAY · ${day.status.toUpperCase()}`;
  return day.plan === null ? 'EDIT DAY · NOT PLANNED' : 'EDIT DAY · PLANNED';
}

export interface NextBlock { date: string; plan: Plan }

export function nextBlock(days: readonly PlanDay[], today: string): NextBlock | null {
  for (const { date, plan } of days) if (date > today && plan !== null && plan.type !== 'rest') return { date, plan };
  return null;
}

export function nextLine({ date, plan }: NextBlock): string {
  const duration = planDuration(plan);
  const time = duration === null ? '' : ` ${duration.text}${duration.defaulted ? ' (DEFAULT)' : ''}`;
  return `${weekdayName(date)} · ${PLAN_LABELS[plan.type]}${time} · ${formatEstimate(plan.estimatedLoad)} LOAD`;
}

export interface TodayPlanView { plan: Plan | null; ridden: Ridden | null; next: NextBlock | null }

export function todayPlanView(days: readonly PlanDay[], today: string): TodayPlanView {
  const day = days.find((d) => d.date === today);
  return { plan: day?.plan ?? null, ridden: day?.status === 'done' ? day.ridden : null, next: nextBlock(days, today) };
}

export interface WeekPlanTotals {
  plannedS: number; plannedLoad: number; riddenS: number; riddenLoad: number; blocks: number; riding: number; done: number;
}

// Planned time uses each type's default where no duration is set; Rest adds a block but no time or riding day.
export function weekPlanTotals(days: readonly PlanDay[]): WeekPlanTotals {
  const totals: WeekPlanTotals = { plannedS: 0, plannedLoad: 0, riddenS: 0, riddenLoad: 0, blocks: 0, riding: 0, done: 0 };
  for (const { plan, ridden, status } of days) {
    totals.riddenS += ridden.movingS;
    totals.riddenLoad += ridden.load;
    if (plan === null) continue;
    totals.blocks += 1;
    totals.plannedLoad += plan.estimatedLoad;
    if (plan.type === 'rest') continue;
    totals.riding += 1;
    totals.plannedS += plan.durationS ?? PLAN_TABLE[plan.type].defaultS;
    if (status === 'done') totals.done += 1;
  }
  return totals;
}

export const progressPercent = (t: WeekPlanTotals): number => (t.plannedLoad === 0 ? 0 : Math.min(100, (t.riddenLoad / t.plannedLoad) * 100));

export function ridingDaysLine(t: WeekPlanTotals): string {
  return t.riding === 0 ? 'NO RIDING DAYS PLANNED' : `${t.done} OF ${t.riding} RIDING ${plural(t.riding, 'DAY')} DONE`;
}

export function copySourceLine(start: string, t: WeekPlanTotals): string {
  return `FROM ${weekRangeLabel(start)} · ${t.blocks} ${plural(t.blocks, 'BLOCK')} · ${formatHoursMinutes(t.plannedS)} · ${formatEstimate(t.plannedLoad)} LOAD`;
}

export function weekOffsetLabel(start: string, thisWeek: string): string {
  const weeks = Math.round(daysBetween(thisWeek, start) / 7);
  if (weeks === 0) return 'THIS WEEK';
  if (weeks === 1) return 'NEXT WEEK';
  if (weeks === -1) return 'LAST WEEK';
  return weeks > 1 ? `IN ${weeks} WEEKS` : `${-weeks} WEEKS AGO`;
}

// ISO 8601: a week belongs to the year of its Thursday, and week 1 holds the year's first Thursday.
export function isoWeek(date: string): number {
  const thursday = addDays(weekStart(date), 3);
  return Math.floor(daysBetween(`${thursday.slice(0, 4)}-01-01`, thursday) / 7) + 1;
}
